import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

export const getCsrfToken = () => document.cookie.split('; ').find((cookie) => cookie.startsWith('csrf_token='))?.split('=')[1] || '';

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// Handle rate limit and other errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      const err = new Error('Too many submissions. Please try again later.');
      err.status = 429;
      return Promise.reject(err);
    }
    if (error.response?.status === 400) {
      const err = new Error(error.response?.data?.detail || 'Invalid request');
      err.status = 400;
      return Promise.reject(err);
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getAdminProjects = () => api.get('/admin/projects');
export const getProject = (slug) => api.get(`/projects/${slug}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);

// Services
export const getServices = () => api.get('/services');
export const getService = (slug) => api.get(`/services/${slug}`);
export const createService = (data) => api.post('/services', data);
export const updateService = (id, data) => api.put(`/services/${id}`, data);
export const deleteService = (id) => api.delete(`/services/${id}`);

// Sectors
export const getSectors = () => api.get('/sectors');
export const getSector = (slug) => api.get(`/sectors/${slug}`);
export const createSector = (data) => api.post('/sectors', data);
export const updateSector = (id, data) => api.put(`/sectors/${id}`, data);
export const deleteSector = (id) => api.delete(`/sectors/${id}`);

// Inquiries
export const submitInquiry = (data) => api.post('/inquiries', data);
export const getInquiries = (params) => api.get('/inquiries', { params });
export const getInquiry = (id) => api.get(`/inquiries/${id}`);
export const updateInquiry = (id, params) => api.put(`/inquiries/${id}`, null, { params });
export const deleteInquiry = (id) => api.delete(`/inquiries/${id}`);
export const downloadInquiryAttachment = (id) => api.get(`/inquiries/${id}/attachment`, { responseType: 'blob' });

export const getInquiryErrorMessage = (error, fallback = 'Unable to send this request right now.') => {
  const detail = error?.response?.data?.detail;
  const message = typeof detail === 'string' && detail.trim() ? detail : error?.message;
  if (/file too large|maximum 10\s*mb|max 10\s*mb/i.test(message || '')) return 'This file exceeds the 10 MB maximum.';
  if (/invalid file type|please upload a pdf|unsupported.*file/i.test(message || '')) return "This file type isn't supported. Upload PDF, JPG, JPEG, PNG or WEBP.";
  if (error?.status === 429 || error?.response?.status === 429) return 'Please wait before sending another submission.';
  if (typeof message === 'string' && message.trim() && !/request failed with status code/i.test(message)) return message;
  return fallback;
};

// Testimonials
export const getTestimonials = () => api.get('/testimonials');
export const getAdminTestimonials = () => api.get('/admin/testimonials');
export const createTestimonial = (data) => api.post('/testimonials', data);
export const updateTestimonial = (id, data) => api.put(`/testimonials/${id}`, data);
export const deleteTestimonial = (id) => api.delete(`/testimonials/${id}`);

// Team
export const getTeam = () => api.get('/team');
export const getAdminTeam = () => api.get('/admin/team');
export const createTeamMember = (data) => api.post('/team', data);
export const updateTeamMember = (id, data) => api.put(`/team/${id}`, data);
export const deleteTeamMember = (id) => api.delete(`/team/${id}`);

// Resources
export const getResources = () => api.get('/resources');
export const getAdminResources = () => api.get('/admin/resources');
export const createResource = (data) => api.post('/resources', data);
export const updateResource = (id, data) => api.put(`/resources/${id}`, data);
export const deleteResource = (id) => api.delete(`/resources/${id}`);
export const getPublicResourceDownloadUrl = (id) => `${API_BASE}/resources/${id}/download`;
export const downloadAdminResource = (id) => api.get(`/admin/resources/${id}/download`, { responseType: 'blob' });

// Settings
export const getSettings = () => api.get('/settings');
export const getAdminSettings = () => api.get('/admin/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getAdminMediaImages = () => api.get('/admin/media/images');

// Upload
export const uploadImage = (file) => {
  const formData = file instanceof FormData ? file : (() => {
    const fd = new FormData();
    fd.append('file', file);
    return fd;
  })();
  return api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadDocument = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/document', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const INQUIRY_ATTACHMENT_COPY = 'Accepted files: PDF, JPG, JPEG, PNG, WEBP · Maximum 10 MB';
export const validateInquiryAttachment = (file) => {
  const extension = file?.name?.split('.').pop()?.toLowerCase();
  if (!['pdf', 'jpg', 'jpeg', 'png', 'webp'].includes(extension)) return "This file type isn't supported. Upload PDF, JPG, JPEG, PNG or WEBP.";
  if (file.size > 10 * 1024 * 1024) return 'This file exceeds the 10 MB maximum.';
  return '';
};
export const uploadInquiryAttachment = (file) => {
  const validationError = validateInquiryAttachment(file);
  if (validationError) return Promise.reject(new Error(validationError));
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/inquiry-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Project Stories
export const getProjectStories = (params) => api.get('/project-stories', { params });
export const getProjectStory = (id) => api.get(`/project-stories/${id}`);
export const createProjectStory = (data) => api.post('/project-stories', data);
export const updateProjectStory = (id, data) => api.put(`/project-stories/${id}`, data);
export const deleteProjectStory = (id) => api.delete(`/project-stories/${id}`);

// Before/After Gallery
export const getBeforeAfter = (params) => api.get('/before-after', { params });
export const getAdminBeforeAfter = () => api.get('/admin/before-after');
export const createBeforeAfter = (data) => api.post('/before-after', data);
export const updateBeforeAfter = (id, data) => api.put(`/before-after/${id}`, data);
export const deleteBeforeAfter = (id) => api.delete(`/before-after/${id}`);

// Blog Posts
export const getBlogPosts = (params) => api.get('/blog/posts', { params });
export const getBlogPost = (slug) => api.get(`/blog/posts/${slug}`);
export const getAdminBlogPosts = () => api.get('/admin/blog/posts');
export const createBlogPost = (data) => api.post('/admin/blog/posts', data);
export const updateBlogPost = (id, data) => api.put(`/admin/blog/posts/${id}`, data);
export const getBlogCategories = () => api.get('/blog/categories');

// Materials
export const getMaterials = (params) => api.get('/materials', { params });
export const getAdminMaterials = (params) => api.get('/admin/materials', { params });
export const createMaterial = (data) => api.post('/admin/materials', data);
export const updateMaterial = (id, data) => api.put(`/admin/materials/${id}`, data);

// Google Reviews
export const getGoogleReviews = (params) => api.get('/google-reviews', { params });
export const createGoogleReview = (data) => api.post('/google-reviews', data);
export const updateGoogleReview = (id, data) => api.put(`/google-reviews/${id}`, data);
export const deleteGoogleReview = (id) => api.delete(`/google-reviews/${id}`);
export const getGoogleReviewSettings = () => api.get('/google-reviews-settings');
export const updateGoogleReviewSettings = (data) => api.put('/google-reviews-settings', data);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Seed
export const seedData = () => api.post('/seed');

export default api;
